/** Error object Cove hands to `onError`. Only the members we read are declared. */
export interface CoveWidgetError {
  code: string;
  message: string;
  /** Machine-readable cause from Cove's boot envelope, e.g. 'widget_disabled'. */
  reason?: string;
}

/**
 * Boot-time codes after which a Cove instance can never chat on this page load.
 *
 * Every realistic cause — a wrong or disabled widget id, a denied origin, a bad
 * container, an outage — is sticky, so a consumer that sees one of these should
 * retire its entry point rather than invite a retry into the same dead panel.
 *
 * Every *other* code Cove reports is a runtime error on a widget that booted
 * (`REHYDRATE_FAILED`, `AUTHENTICATE_FAILED`, `CONVERSATION_CREATE_FAILED`, …).
 * Cove renders its own in-panel state for those and the visitor can carry on, so
 * treating them as fatal would take the widget away over a recoverable hiccup.
 */
export const COVE_FATAL_BOOT_CODES: ReadonlySet<string> = new Set([
  'WIDGET_DISABLED',
  'ORIGIN_DENIED',
  'MISSING_API_URL',
  'INVALID_CONTAINER',
  'SERVER_UNAVAILABLE',
  'CORS_ERROR',
]);

/** Whether this error means the widget will never work on this page load. */
export function isFatalCoveBootError(error: CoveWidgetError): boolean {
  return COVE_FATAL_BOOT_CODES.has(error.code);
}

/** Handle returned by Cove's init(). Only the members we use are declared. */
export interface CoveWidgetInstance {
  /** Open the chat panel (floating mode: shows the panel; no launcher needed). */
  open(): void;
  close(): void;
  /**
   * Close the current conversation server-side, clear Cove's sessionStorage
   * entry, and reset to the welcome state. Idempotent with no conversation.
   */
  startOver(): void;
  destroy(): void;
  /**
   * Host-site identity: hand Cove the visitor's API bearer token (kept in
   * memory only) or `null` to retract it. Returns false on a disabled or
   * destroyed instance. Must be called synchronously after init() for a
   * rehydrated conversation's first request to carry the token.
   */
  authenticate(token: string | null): boolean;
}

export interface CoveInitConfig {
  widgetId: string;
  /**
   * Embedded Mode: Cove mounts its own shadow root inside this element.
   * Omit for the normal floating launcher mode.
   */
  container?: HTMLElement;
  /** Floating mode only: never show the launcher bubble (the host opens the panel). */
  hidden?: boolean;
  /**
   * Whether Cove restores the previous conversation in this tab (sessionStorage).
   * Cove defaults it to true. The contact form passes false, which makes its
   * mode toggle destructive; the member portal keeps the default.
   */
  persistSession?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: CoveWidgetError) => void;
}

export interface CoveWidgetGlobal {
  init(config: CoveInitConfig): CoveWidgetInstance;
}

declare global {
  interface Window {
    CoveWidget?: CoveWidgetGlobal;
  }
}

export const COVE_SCRIPT_ID = 'kuow-cove-embed-script';

/**
 * How long to wait for the embed script before giving up.
 *
 * A stalled request fires neither `load` nor `error`, so without this the
 * promise would stay pending, `SupportAgentPanel` would never learn the agent
 * is unavailable, and the visitor would sit on an empty panel until the browser
 * itself gave up — possibly minutes. Rejecting routes into the same fallback as
 * any other failure: the dialog shows the form and drops the agent toggle.
 * The member portal's provider likewise depends on this rejection to toast and
 * hide its Need Help? control.
 */
const LOAD_TIMEOUT_MS = 10_000;

let loadPromise: Promise<CoveWidgetGlobal> | null = null;

/**
 * Load Cove's tenant-scoped embed bundle exactly once per page.
 *
 * Cove's init() singleton is scoped to a *loaded bundle*, not the page: a second
 * copy of this script mounts a second independent widget with no warning, and
 * destroy() only tears down its own. The contact widget mounts once per contact
 * link (nav, sub-nav, footer) and the member portal mounts its provider once per
 * page, so every consumer must share one load. The document-level id check
 * covers the case where another copy of this module — or a hand-authored
 * snippet — already injected the bundle.
 *
 * No `apiUrl` is needed by callers: Cove derives its API base from this script
 * element's own src via document.currentScript.
 */
export function loadCoveEmbed(src: string): Promise<CoveWidgetGlobal> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<CoveWidgetGlobal>((resolve, reject) => {
    if (window.CoveWidget) {
      resolve(window.CoveWidget);
      return;
    }

    const existing = document.getElementById(COVE_SCRIPT_ID);
    const reused = existing instanceof HTMLScriptElement ? existing : null;
    const script = reused ?? document.createElement('script');

    const timer = setTimeout(() => {
      cleanup();
      // Same reasoning as the two rejection paths below: drop the cache and the
      // element so a later dialog open injects a fresh script.
      loadPromise = null;
      script.remove();
      reject(new Error(`Timed out loading Cove embed script: ${src}`));
    }, LOAD_TIMEOUT_MS);

    function cleanup(): void {
      clearTimeout(timer);
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    }

    function handleLoad(): void {
      cleanup();
      const global = window.CoveWidget;
      if (!global) {
        // Drop the cache and the dead element so a later dialog open can retry:
        // this script's load event already fired and will never fire again.
        loadPromise = null;
        script.remove();
        reject(new Error('Cove embed loaded but the CoveWidget global is missing'));
        return;
      }
      resolve(global);
    }

    function handleError(): void {
      cleanup();
      // Drop the cache and the dead element so a later dialog open can retry:
      // this script's error event already fired and will never fire again.
      loadPromise = null;
      script.remove();
      reject(new Error(`Failed to load Cove embed script: ${src}`));
    }

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    if (!reused) {
      script.id = COVE_SCRIPT_ID;
      script.src = src;
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return loadPromise;
}
